import classNames from 'classnames';
import fragment from './ActivityTotalsFragment.gql';
import PageSection from '../PageSection/PageSection';
import React, {
  FunctionComponent,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import styles from './ActivityTotals.scss';
import { ActionLinks } from './ActionLinks/ActionLinks';
import { ExtraMeasurements } from './ExtraMeasurements/ExtraMeasurements';
import { FilterButtons } from './FilterButtons/FilterButtons';
import { FormattedDistance } from '../Formatters/FormattedDistance';
import {
  getMeasurementSystemFromSettings,
  getDistanceUnitForMeasurementSystem,
} from '../../utils/measurementSystem';
import { ProgressBar } from './ProgressBar/ProgressBar';
import { useFlags } from '../../hooks/useFlags';
import { useTranslation } from 'react-i18next';
import {
  ActivityTotalsFragment,
  PageTotal,
  PageTotalsDisplayOptions,
  TimeUnit,
  DistanceUnit,
  MeasurementSystem,
} from '../../../types/__generated__';
import usePageQuery, {
  buildQuery,
  UsePageQueryResult,
} from '../../hooks/usePageQuery';
import {
  ActivityFilters,
  buildFilteredTotals,
  compareActivityTotalsMaps,
  ActivityTotalsMap,
  ActivityTotal,
} from './filters';

const FRAGMENT = buildQuery(fragment);
const REFETCH_POLLING_INTERVAL = 2000;

export const AnyDistance = 'any:distance';

export type ActivityTotalsFlags = {
  virtualFitnessEventsPreview: boolean;
  virtualFitnessEventsBleedingEdge: boolean;
};

export const ActivityTotals: FunctionComponent = () => {
  let [activityFilter, setActivityFilter] = useState(ActivityFilters.all.key);
  let [previousTotalsMap, setPreviousTotalsMap] = useState<ActivityTotalsMap>(
    {},
  );
  const { t, i18n } = useTranslation('common', { useSuspense: false });

  const {
    virtualFitnessEventsPreview,
    virtualFitnessEventsBleedingEdge,
  } = useFlags<ActivityTotalsFlags>();
  if (!virtualFitnessEventsPreview) {
    return null;
  }

  // Avoid querying GraphQL if feature flag is disabled.
  // This flag is configured only per environment, so it's safe to use usePageQuery conditionally.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const result = usePageQuery<ActivityTotalsFragment>(FRAGMENT, {
    notifyOnNetworkStatusChange: true,
  });

  if (!shouldShowTotaliser(UsePageQueryResult.hasData(result), result?.data)) {
    return null;
  }

  const settings = result.data?.application?.settings;
  const measurementSystem = getMeasurementSystemFromSettings(i18n, settings);
  const timelineTotals: PageTotal[] = result.data?.totals?.timeline ?? [];

  const { activityTypes, activityTotalsMap } = buildViewData(
    timelineTotals,
    measurementSystem,
    activityFilter,
    setActivityFilter,
  );

  if (!compareActivityTotalsMaps(activityTotalsMap, previousTotalsMap)) {
    result.stopPolling();
    setPreviousTotalsMap(activityTotalsMap);
  }

  const refetchTotals = () => {
    result.startPolling(REFETCH_POLLING_INTERVAL);
  };

  const totals = activityTotalsMap[activityFilter];

  return (
    <PageSection className={styles.activityTotalsSection}>
      <div className={styles.box}>
        <FilterButtons
          activityTypes={activityTypes}
          activityFilter={activityFilter}
          setActivityFilter={setActivityFilter}
        />
        <div className={classNames('jg-text--regular-light', styles.summary)}>
          <strong>
            <FormattedDistance value={totals.distance} />
          </strong>
          {activityFilter === ActivityFilters.all.key &&
            ` ${t('activity.totals.completedOverall')}`}
        </div>
        {virtualFitnessEventsBleedingEdge && (
          <ProgressBar percent={55}></ProgressBar>
        )}
        {virtualFitnessEventsBleedingEdge && (
          <ExtraMeasurements
            duration={totals.duration}
            elevation={totals.elevation}
          />
        )}
        <ActionLinks refetchTotals={refetchTotals} />
      </div>
    </PageSection>
  );
};

const shouldShowTotaliser = (
  hasData: boolean,
  data: ActivityTotalsFragment | undefined | null,
): boolean => {
  const totaliserDisplayOptions = hasData ? data?.totals?.displayOptions : null;
  if (
    !totaliserDisplayOptions ||
    totaliserDisplayOptions === PageTotalsDisplayOptions.DoNotDisplay
  ) {
    return false;
  }

  return true;
};

const buildViewData = (
  timelineTotals: PageTotal[],
  measurementSystem: MeasurementSystem,
  activityFilter: string,
  setActivityFilter: Dispatch<SetStateAction<string>>,
) => {
  if (timelineTotals.length === 0) {
    return {
      activityTypes: [ActivityFilters.all.key],
      activityTotalsMap: {
        [ActivityFilters.all.key]: buildDefaultZeroTotal(
          getDistanceUnitForMeasurementSystem(measurementSystem),
        ),
      },
    };
  } else {
    const {
      activityTotalsMap,
      activityTypes,
      isSingleActivityMode,
    } = buildFilteredTotals(measurementSystem, timelineTotals);

    if (isSingleActivityMode && activityFilter === ActivityFilters.all.key) {
      // The initial value of activityFilter is "all".
      // However, we remove "all" if there is only one activity type.
      // So, switch the current filter over to that one type.
      setActivityFilter(activityTypes[0]);
    }

    return {
      activityTypes,
      activityTotalsMap,
    };
  }
};

const buildDefaultZeroTotal = (distanceUnit: DistanceUnit): ActivityTotal => ({
  distance: {
    unit: distanceUnit,
    value: 0,
  },
  duration: {
    unit: TimeUnit.Seconds,
    value: 0,
  },
  elevation: {
    unit: distanceUnit,
    value: 0,
  },
});
